import React, {useCallback, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {clearSelection} from 'src/components/data-table-selection';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {SortDirection, SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {UserSortField as UserSortFields} from 'src/openapi/admin/models/UserSortField';

import {SortHeaderWithDataTable} from '@pb/kit-component-sort-header';

import {COLUMNS, HEADERS, SELECTION_KEY, SORTING_WARNING} from '../../constants';
import {useGetUserSearchResultCrmIds} from '../../hooks/useGetUserSearchResultCrmIds';
import {
    userSearchResultStateActions,
    userSearchResultStateSelector,
} from '../../slices/user-search-result-state.slice';
import {userSearchResultResultSelector} from '../../slices/user-search-result.slice';

import {HeaderContentBefore} from './header-caption';
import {HeaderSkeleton} from './header-skeleton';

type Props = {
    userIds: number[],
    totalCount: number,
};

const MsDynamicsIdHeaderController = ({userIds, totalCount}: Props) => {
    const dispatch = useDispatch();
    const {setErrorMessage} = useContext(MessageContext);

    const cacheKeys = useSelector(userSearchResultStateSelector);
    const {isLoading, loadingError} = useGetUserSearchResultCrmIds({
        userIds,
        totalCount,
        columnId: COLUMNS.MS_DYNAMICS_ID,
    });

    const sort = useCallback((direction: ('ASC' | 'DESC')) => {
        if (totalCount > MAX_SELECTED_ENTITIES) {
            setErrorMessage(SORTING_WARNING);
            return;
        }

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(userSearchResultStateActions.setSortCriteria({
            direction: direction as SortDirection,
            field: UserSortFields.MS_DYNAMICS_ID,
        }));
    }, [totalCount, dispatch, setErrorMessage]);

    return (
        <SortHeaderWithDataTable
            defaultDirection={SortDirections.ASC}
            direction={cacheKeys.sortCriteria.field === UserSortFields.MS_DYNAMICS_ID
                ? cacheKeys.sortCriteria.direction
                : null}
            filterActive={true}
            caption={HEADERS[COLUMNS.MS_DYNAMICS_ID]}
            contentBefore={(isLoading || loadingError)
                ? <HeaderContentBefore isLoading={isLoading} loadingError={loadingError}/>
                : undefined}
            onSort={sort}
        />
    );
};

export const MsDynamicsIdHeader = () => {
    const result = useSelector(userSearchResultResultSelector);

    return result ? (
        <MsDynamicsIdHeaderController
            userIds={result.data.map(({id}) => id)}
            totalCount={result.totalCount}
        />
    ) : (
        <HeaderSkeleton columnId={COLUMNS.MS_DYNAMICS_ID}/>
    );
};
